import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  MoreHorizontal,
  Archive,
  ArchiveRestore,
  Pencil,
  LogIn,
  RotateCcw,
  ListTodo,
  Layers,
  Check,
  X,
} from 'lucide-react';
import { useHistory, useParams } from 'react-router-dom';
import { Button } from '../../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import { TableCell, TableRow } from '../../../../../components/ui/table';
import { BigHead } from '../../../../../lib/core.cjs.development';
import { cn } from '../../../../../lib/utils';
import apiService from '../../../../../services/api.service';
import getUserLastLoginDate from '../../../../../utils/lastLoginDate';
import EnglishLevelsEnum from '../../../../Admin/Users/enums/englishLevels.enum';
import ConfirmModal from './ConfirmModal';
import LevelAutocomplete from './LevelAutocomplete';

function UsersTableRow({ user, onArchive, onUnArchive, growLevels, onSave }) {
  const history = useHistory();
  const { department } = useParams();

  const [editable, setEditable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [currentLevel, setCurrentLevel] = useState({ key: user.level });
  const [currentGoal, setCurrentGoal] = useState({ key: user.goal });
  const [currentEnglishLevel, setCurrentEnglishLevel] = useState(null);
  const [resetProgressModal, setResetProgressModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', changes: [] });
  const [reviews, setReviews] = useState([]);
  const [initialState, setInitialState] = useState({
    level: null,
    goal: null,
    englishLevel: null,
  });

  const englishLevelsArray = Object.entries(EnglishLevelsEnum).map(([key, { display, value }]) => ({
    key,
    display,
    value,
  }));

  useEffect(() => {
    apiService.head.getDepartmentReviews(department).then((data) => {
      setReviews(data);
    });
  }, [department]);

  useEffect(() => {
    if (growLevels.length) {
      const foundLevel = growLevels.find((lvl) => lvl.key === user.level);
      const foundGoal = growLevels.find((lvl) => lvl.key === user.goal);
      const foundEnglishLevel = user.englishLevel
        ? englishLevelsArray.find((item) => item.value === user.englishLevel)
        : null;

      setCurrentLevel(foundLevel);
      setCurrentGoal(foundGoal);
      setCurrentEnglishLevel(foundEnglishLevel);
      setInitialState({
        level: foundLevel,
        goal: foundGoal,
        englishLevel: foundEnglishLevel,
      });
    }
  }, [user.goal, growLevels, user.level, user.englishLevel]);

  const hasReviewProcessed = () => {
    return reviews.some((review) => {
      const { status, goal: reviewGoal, userId } = review;
      return (
        status === 'PROCESSED' &&
        userId === user._id &&
        (reviewGoal === user.goal || reviewGoal === currentGoal?._id)
      );
    });
  };

  const handleCancel = () => {
    setEditable(false);
    setCurrentLevel(initialState.level);
    setCurrentGoal(initialState.goal);
    setCurrentEnglishLevel(initialState.englishLevel);
  };

  const handleSave = async () => {
    try {
      if (hasReviewProcessed()) {
        throw new Error('User has pending review!');
      }

      setIsSaving(true);

      const body = {
        ...(currentLevel._id !== initialState.level._id && { level: currentLevel._id }),
        ...(currentGoal._id !== initialState.goal._id && { goal: currentGoal._id }),
        ...(currentEnglishLevel !== initialState.englishLevel && {
          englishLevel: currentEnglishLevel?.value,
        }),
      };

      if (currentEnglishLevel === initialState.englishLevel) {
        await apiService.head.upgradeUserGoal(user._id, currentGoal._id, true);
      }
      await apiService.admin.updateUser(user._id, body);

      setIsSaving(false);
      setEditable(false);
      setInitialState({
        level: currentLevel,
        goal: currentGoal,
        englishLevel: currentEnglishLevel,
      });
      onSave();
    } catch (err) {
      setError(`Save error: ${err.message}`);
      setIsSaving(false);
      handleCancel();
    }
  };

  const handleValidate = () => {
    const changesArray = [];

    if (initialState.level?._id !== currentLevel?._id) {
      changesArray.push({
        field: 'Level',
        from: initialState.level?.key,
        to: currentLevel?.key,
      });
    }

    if (initialState.goal?._id !== currentGoal?._id) {
      changesArray.push({
        field: 'Goal',
        from: initialState.goal?.key,
        to: currentGoal?.key,
      });
    }

    if (initialState.englishLevel !== currentEnglishLevel) {
      changesArray.push({
        field: 'English',
        from: initialState.englishLevel?.display || initialState.englishLevel?.value,
        to: currentEnglishLevel?.display || currentEnglishLevel?.value,
      });
    }

    if (changesArray.length > 0) {
      setConfirmModal({
        isOpen: true,
        message: 'Are you sure you want to save these changes?',
        changes: changesArray,
      });
    }
    setEditable(false);
  };

  const handleResetProgress = async () => {
    try {
      if (hasReviewProcessed()) {
        throw new Error('User has pending review!');
      }

      const body = {
        level: currentLevel._id,
        goal: currentGoal._id,
        progress: 0,
      };

      await apiService.head.upgradeUserGoal(user._id, currentGoal._id, true);
      await apiService.admin.updateUser(user._id, body);
      setResetProgressModal(false);
      onSave();
    } catch (err) {
      setError(`Reset progress error: ${err.message}`);
      setResetProgressModal(false);
    }
  };

  const handleLoginAs = async () => {
    const token = await apiService.head.signInAs(user._id);
    window.open(`${process.env.REACT_APP_URL}/home?guestToken=${token}`, '_blank');
  };

  const handleEditSkills = () => {
    history.push(`/head/departments/${department}/users/${user._id}`);
  };

  const handlePersonalTasks = () => {
    history.push(`/head/departments/${department}/users/${user._id}/personal-tasks/`);
  };

  const updateCurrentLevel = (newValue) => {
    if (!newValue) return;
    setCurrentLevel(newValue);
    if (newValue.position > currentGoal?.position) {
      setCurrentGoal(newValue);
    }
  };

  return (
    <>
      <TableRow className={cn(user.isArchived && 'opacity-50')}>
        {/* Avatar */}
        <TableCell className="p-2">
          <div className="h-10 w-10 rounded-full overflow-hidden bg-muted">
            <BigHead {...user.avatar} />
          </div>
        </TableCell>

        {/* User Info */}
        <TableCell>
          <div>
            <p className="font-medium text-sm">{user.email}</p>
            {(user.firstName || user.lastName) && (
              <p className="text-xs text-muted-foreground">
                {user.firstName} {user.lastName}
              </p>
            )}
          </div>
        </TableCell>

        {/* Current Level */}
        <TableCell className="text-center">
          {editable ? (
            <LevelAutocomplete
              options={growLevels}
              value={currentLevel}
              onChange={updateCurrentLevel}
              getOptionLabel={(option) => option?.key || ''}
            />
          ) : (
            <span
              className={cn(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                user.isArchived
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-green-500/10 text-green-600',
              )}
            >
              {currentLevel?.key || user.level}
            </span>
          )}
        </TableCell>

        {/* Progress */}
        <TableCell className="text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  user.isArchived ? 'bg-muted-foreground' : 'bg-primary',
                )}
                style={{ width: `${user.progress || 0}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground w-10">{user.progress || 0}%</span>
          </div>
        </TableCell>

        {/* Goal */}
        <TableCell className="text-center">
          {editable ? (
            <LevelAutocomplete
              options={growLevels}
              value={currentGoal}
              onChange={setCurrentGoal}
              getOptionLabel={(option) => option?.key || ''}
              getOptionDisabled={(option) => option.position < currentLevel?.position}
            />
          ) : (
            <span
              className={cn(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                user.isArchived
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-amber-500/10 text-amber-600',
              )}
            >
              {currentGoal?.key || user.goal}
            </span>
          )}
        </TableCell>

        {/* English Level */}
        <TableCell className="text-center">
          {editable ? (
            <LevelAutocomplete
              options={englishLevelsArray}
              value={currentEnglishLevel}
              onChange={setCurrentEnglishLevel}
              getOptionLabel={(option) => option?.display || ''}
            />
          ) : (
            <span className="text-sm">
              {currentEnglishLevel?.value || user.englishLevel || '-'}
            </span>
          )}
        </TableCell>

        {/* Last Login */}
        <TableCell className="text-center">
          <span className="text-sm text-muted-foreground">
            {getUserLastLoginDate(user.lastLoginAt).split(',')[0]}
          </span>
        </TableCell>

        {/* Actions */}
        <TableCell>
          {editable ? (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleValidate} disabled={isSaving}>
                <Check className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!user.isArchived && (
                  <DropdownMenuItem onClick={() => onArchive(user._id)}>
                    <Archive className="h-4 w-4 mr-2" />
                    Archive
                  </DropdownMenuItem>
                )}
                {user.isArchived && (
                  <DropdownMenuItem onClick={() => onUnArchive(user._id)}>
                    <ArchiveRestore className="h-4 w-4 mr-2" />
                    Unarchive
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => setEditable(true)}
                  disabled={!growLevels.length || user.isArchived}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleEditSkills} disabled={user.isArchived}>
                  <Layers className="h-4 w-4 mr-2" />
                  Edit Skills
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLoginAs} disabled={user.isArchived}>
                  <LogIn className="h-4 w-4 mr-2" />
                  Log in as
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handlePersonalTasks} disabled={user.isArchived}>
                  <ListTodo className="h-4 w-4 mr-2" />
                  Personal tasks
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setResetProgressModal(true)}
                  disabled={user.isArchived}
                  className="text-destructive"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset progress
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TableCell>
      </TableRow>

      {/* Reset Progress Confirmation Modal */}
      <ConfirmModal
        isOpen={resetProgressModal}
        onClose={() => setResetProgressModal(false)}
        onConfirm={handleResetProgress}
        title="Reset Progress"
        message="Are you sure you want to reset this user's progress?"
      />

      {/* Save Changes Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => {
          setConfirmModal({ isOpen: false, message: '', changes: [] });
          handleCancel();
        }}
        onConfirm={() => {
          setConfirmModal({ isOpen: false, message: '', changes: [] });
          handleSave();
        }}
        title="Save Changes"
        message={confirmModal.message}
        changes={confirmModal.changes}
      />

      {/* Error Snackbar - using native alert for simplicity */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-destructive text-destructive-foreground px-4 py-2 rounded-md">
          {error}
          <button type="button" onClick={() => setError('')} className="ml-2 font-bold">
            ×
          </button>
        </div>
      )}
    </>
  );
}

UsersTableRow.propTypes = {
  user: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    avatar: PropTypes.shape({}).isRequired,
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    goal: PropTypes.string.isRequired,
    lastLoginAt: PropTypes.string,
    hydraProfileLink: PropTypes.string,
    level: PropTypes.string.isRequired,
    englishLevel: PropTypes.string,
    progress: PropTypes.number,
    isArchived: PropTypes.bool,
  }).isRequired,
  onArchive: PropTypes.func.isRequired,
  onUnArchive: PropTypes.func.isRequired,
  growLevels: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      _id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onSave: PropTypes.func.isRequired,
};

export default UsersTableRow;
